import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@istari-frontend/ui";
import { AccessRelationship } from "@istari/istari-client";
import { Globe, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  useGetArtifactAccessById,
  useGetModelAccessById,
  useGetResourceAccessById,
  useShareFile,
} from "~/api/useModels";
import { useGetSystemAccessById, useShareSystem } from "~/api/useSystems";
import { ThreeDotsFade } from "~/assets/svg";
import { AccessActionType, ResourceType, UserGrantType } from "~/enums";
import { getCanUserViewOnly } from "~/permissions";
import { useResource } from "~/resource/data-source/use-resource";
import { useUsers } from "~/user/data-source/use-users";
import { useCurrentUserIdentity } from "~/user/feature/use-current-user-identity";
import { findCurrentRegistryUser } from "~/user/model/current-user-identity";
import { sortArrayByKeyAndOrder } from "~/utils/array";
import { validateEmail } from "~/utils/strings";

import { PermissionSelect } from "./permission-select";
import { SendingShareView } from "./sending-share-view";
import { SharedUserList, SharedUserListItem } from "./shared-user-list";
import { UserSearchInput } from "./user-search-input";

type DraftUserType = {
  created?: Date | string | undefined;
  displayName?: string | undefined | null;
  email?: string | undefined | null;
  firstName?: string | undefined | null;
  id?: string | undefined;
  lastName?: string | undefined | null;
  providerName?: string | undefined;
  state?: string | undefined;
  userName?: string | undefined | null;
  grant?: string;
  providerUserId?: string | undefined;
  relation?: string | undefined;
  resourceId?: string;
  resourceType?: string;
  subjectId?: string;
  userId: string | undefined;
  subjectType?: string;
  label?: string;
  value?: string;
  justAdded?: boolean;
  subjectInfo?: {
    email?: string;
    username?: string;
  };
};

type ManageAccessModalProps = {
  open: boolean;
  resourceType: ResourceType.Model | ResourceType.Artifact | ResourceType.System | ResourceType.Resource;
  title: string;
  artifactId?: string;
  modelId?: string;
  systemId?: string;
  /** A plain v3 file, a connected file or a container: one `resource` object, keyed by resource id. */
  resourceId?: string;
  // Which panel to land on when the dialog opens. Defaults to "users". Ignored when
  // remote sharing isn't applicable for this resource (System, no permission, etc.) —
  // the dialog falls back to the users panel in that case.
  initialMode?: "users" | "remote";
  setModalOpen: (open: boolean) => void;
};

const SHARE_SUBJECT: Record<ManageAccessModalProps["resourceType"], string> = {
  [ResourceType.Model]: "this model",
  [ResourceType.Artifact]: "this artifact",
  [ResourceType.Resource]: "this resource",
  [ResourceType.System]: "this system",
};

export default function ManageAccessModal(props: ManageAccessModalProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.setModalOpen}>
      {/* Unmount-on-close resets shareMode and draft state for the next open. */}
      {props.open && <ManageAccessModalContent {...props} />}
    </Dialog>
  );
}

type ManageAccessModalContentProps = Omit<ManageAccessModalProps, "open">;

// eslint-disable-next-line complexity -- TODO fix later
function ManageAccessModalContent({
  artifactId,
  modelId,
  systemId,
  resourceId,
  resourceType,
  title,
  initialMode = "users",
  setModalOpen,
}: ManageAccessModalContentProps) {
  const identity = useCurrentUserIdentity();

  const orgUsersQuery = useUsers();
  const modelAccessQuery = useGetModelAccessById(modelId, resourceType === ResourceType.Model);
  const artifactAccessQuery = useGetArtifactAccessById(artifactId, resourceType === ResourceType.Artifact);
  const systemAccessQuery = useGetSystemAccessById(systemId, resourceType === ResourceType.System);
  const resourceAccessQuery = useGetResourceAccessById(resourceId, resourceType === ResourceType.Resource);

  const shareFileMutation = useShareFile();
  const shareSystemMutation = useShareSystem();

  const [userGrants, setUserGrants] = useState<DraftUserType[]>([]);
  const [draftUsersWithAccess, setDraftUsersWithAccess] = useState<DraftUserType[]>([]);
  const [shareMode, setShareMode] = useState<"users" | "remote">(initialMode);
  const hasInitializedRef = useRef(false);

  // user.label is already the resolved display name (userToInternalUser); do not
  // rebuild it from firstName/lastName, which identity v2 users may not have.
  const listOfOrgUsers = orgUsersQuery.data?.map((user) => ({
    ...user,
    value: user.id,
    userId: user.id,
  }));

  const currentAccessList =
    resourceType === ResourceType.Model
      ? modelAccessQuery.data
      : resourceType === ResourceType.Artifact
        ? artifactAccessQuery.data
        : resourceType === ResourceType.Resource
          ? resourceAccessQuery.data
          : systemAccessQuery.data;

  const currentUser = findCurrentRegistryUser(orgUsersQuery.data, identity);
  const currentUserPermission = currentAccessList?.find(
    (userGrant: AccessRelationship) => userGrant.subjectId === currentUser?.id,
  )?.relation as UserGrantType;

  const canUserViewOnly = getCanUserViewOnly(currentUserPermission);

  const isLoading =
    orgUsersQuery.isLoading ||
    (resourceType === ResourceType.Model && modelAccessQuery.isLoading) ||
    (resourceType === ResourceType.Artifact && artifactAccessQuery.isLoading) ||
    (resourceType === ResourceType.System && systemAccessQuery.isLoading) ||
    (resourceType === ResourceType.Resource && resourceAccessQuery.isLoading);

  const accessQueryError =
    (resourceType === ResourceType.Model && modelAccessQuery.isError) ||
    (resourceType === ResourceType.Artifact && artifactAccessQuery.isError) ||
    (resourceType === ResourceType.System && systemAccessQuery.isError) ||
    (resourceType === ResourceType.Resource && resourceAccessQuery.isError);

  useEffect(() => {
    // Wait for both access list and org users to finish loading before initializing.
    // This prevents a race condition where the access list loads first but users
    // get filtered out because org user data (firstName, lastName, etc.) isn't available yet.
    // We gate on !isLoading rather than checking data directly so we can still initialize
    // even if org users query fails (users will show as "Unknown User").
    if (hasInitializedRef.current || isLoading || !currentAccessList) {
      return;
    }

    const getUserList = (list: AccessRelationship[] | undefined) =>
      list?.map((userAccess) => {
        const user = orgUsersQuery.data?.find((orgUser) => orgUser.id === userAccess.subjectId);
        return {
          ...user,
          ...userAccess,
          grant: userAccess.relation,
          userId: userAccess.subjectId,
        };
      });

    const userList = getUserList(currentAccessList);
    if (userList) {
      setUserGrants(userList as DraftUserType[]);
      setDraftUsersWithAccess(userList as DraftUserType[]);
      hasInitializedRef.current = true;
    }
  }, [currentAccessList, orgUsersQuery.data, isLoading]);

  const getIdByResourceType = (rt: ResourceType) => {
    if (rt === ResourceType.Model) return modelId;
    if (rt === ResourceType.Artifact) return artifactId;
    if (rt === ResourceType.System) return systemId;
    if (rt === ResourceType.Resource) return resourceId;
    return "";
  };

  // Sharing endpoints split ids: the list endpoint takes the resource id, the share/revoke
  // mutations take the resource's underlying fileId.
  const shareResourceId = resourceType === ResourceType.System ? undefined : (modelId ?? artifactId ?? resourceId);
  const shareResourceQuery = useResource(shareResourceId, resourceType !== ResourceType.System);
  const shareFileId = shareResourceQuery.data?.fileId;

  const handleAddUser = (user: { id?: string; [key: string]: unknown }) => {
    const newUser: DraftUserType = {
      ...user,
      resourceId: getIdByResourceType(resourceType),
      resourceType,
      subjectId: user.id,
      subjectType: "user",
      grant: UserGrantType.viewer,
      justAdded: true,
      userId: user.id,
    };
    setDraftUsersWithAccess((prev) => [...prev, newUser]);
  };

  const handleAddEmail = (email: string) => {
    const newEmailUser: DraftUserType = {
      label: email,
      resourceId: getIdByResourceType(resourceType),
      resourceType,
      subjectId: email,
      subjectType: "user",
      grant: UserGrantType.viewer,
      justAdded: true,
      userId: undefined,
    };
    setDraftUsersWithAccess((prev) => [...prev, newEmailUser]);
  };

  const handleRemoveUserAccess = (removedUserId: string) => {
    setDraftUsersWithAccess((prev) => prev.filter((user) => user.userId !== removedUserId));
  };

  const handleUpdateUserPermission = (userId: string, grant: UserGrantType) => {
    setDraftUsersWithAccess((prev) => prev.map((user) => (user.userId === userId ? { ...user, grant } : user)));
  };

  const handleShare = () => {
    const draftUsersWithAccessIds = draftUsersWithAccess.map((item: DraftUserType) => item.userId);
    const originalGrantByUserId = new Map((userGrants || []).map((u) => [u.userId, u as DraftUserType]));
    const originalUserIds = userGrants.map((item) => item.userId);

    const newUsers = draftUsersWithAccess.filter(
      (usr) => usr.justAdded || (!!usr.label && !originalUserIds.includes(usr.userId)),
    );

    const removedUsers = userGrants.filter((usr) => !draftUsersWithAccessIds.includes(usr.userId));

    const changedUsers = draftUsersWithAccess.filter((usr) => {
      if (!usr.userId || !originalGrantByUserId.has(usr.userId)) return false;
      const original = originalGrantByUserId.get(usr.userId)!;
      if (usr.grant === UserGrantType.owner || original.relation === UserGrantType.owner) return false;
      return (usr.grant as string) !== (original.grant || original.relation);
    });

    const accessGrants = [
      ...newUsers.map((item) => ({
        action: AccessActionType.create,
        userId: item.userId,
        accessRelationship: {
          relation: item.grant,
          resourceId: item.resourceId,
          resourceType,
          subjectId: item.subjectId || item.label,
          subjectType: item.subjectType,
        } as AccessRelationship,
      })),
      ...changedUsers.map((item) => ({
        action: AccessActionType.update,
        userId: item.userId,
        accessRelationship: {
          relation: item.grant,
          resourceId: item.resourceId,
          resourceType,
          subjectId: item.subjectId || item.label,
          subjectType: item.subjectType,
        } as AccessRelationship,
      })),
      ...removedUsers.map((item) => ({
        action: AccessActionType.remove,
        userId: item.userId,
        accessRelationship: {
          relation: item.grant,
          resourceId: item.resourceId,
          resourceType,
          subjectId: item.subjectId || item.label,
          subjectType: item.subjectType,
        } as AccessRelationship,
      })),
    ];

    if (resourceType === ResourceType.System) {
      const shareData = {
        systemId: systemId!,
        accessGrants,
        resourceType,
      };
      shareSystemMutation.mutate(shareData, {
        onSuccess: () => {
          toast.success("System sharing has been successfully updated.");
          setModalOpen(false);
        },
        onError: () => {
          toast.error("An error occurred when sharing the system.");
        },
      });
    } else {
      const shareData = {
        entityId: getIdByResourceType(resourceType),
        accessGrants,
        resourceType,
      };
      shareFileMutation.mutate(shareData, {
        onSuccess: () => {
          toast.success("Resource sharing has been successfully updated.");
          setModalOpen(false);
        },
        onError: () => {
          toast.error("An error occurred when sharing the resource.");
        },
      });
    }
  };

  const givenAccessUserIds = draftUsersWithAccess?.map((user) => user.id);
  const availableUsers = listOfOrgUsers?.filter((user: DraftUserType) => !givenAccessUserIds?.includes(user.id)) || [];
  const sortedAvailableUsers = sortArrayByKeyAndOrder(availableUsers, "label", "asc");

  const displayedUsers =
    resourceType === ResourceType.Artifact
      ? draftUsersWithAccess.filter((user) => user.grant !== UserGrantType.owner)
      : draftUsersWithAccess;

  const sortedDisplayedUsers = displayedUsers
    .filter((u) => u.firstName || u.lastName || u.displayName || u.label || u.subjectInfo?.email || u.subjectId)
    .sort((a, b) => {
      const aIsOwner = a.grant === UserGrantType.owner;
      const bIsOwner = b.grant === UserGrantType.owner;
      if (aIsOwner && !bIsOwner) return -1;
      if (!aIsOwner && bIsOwner) return 1;
      const aName =
        a.displayName ||
        (a.firstName || a.lastName ? [a.firstName, a.lastName].filter(Boolean).join(" ") : "") ||
        a.label ||
        "";
      const bName =
        b.displayName ||
        (b.firstName || b.lastName ? [b.firstName, b.lastName].filter(Boolean).join(" ") : "") ||
        b.label ||
        "";
      return aName.localeCompare(bName);
    });

  const showModeToggleLink =
    resourceType !== ResourceType.System && !!shareResourceId && !!shareFileId && !canUserViewOnly && !isLoading;

  const isRemoteMode = shareMode === "remote" && showModeToggleLink;

  const modeToggleLink = showModeToggleLink ? (
    <button
      type="button"
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs transition-colors"
      onClick={() => setShareMode(isRemoteMode ? "users" : "remote")}
    >
      {isRemoteMode ? (
        <>
          <Users className="size-3" />
          Share with users
        </>
      ) : (
        <>
          <Globe className="size-3" />
          Share with external connection
        </>
      )}
    </button>
  ) : undefined;

  return (
    <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-xl">
      <DialogHeader className="shrink-0">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {canUserViewOnly
            ? `Sharing and access for ${SHARE_SUBJECT[resourceType]}.`
            : isRemoteMode
              ? "Manage which secure connections have access to this resource."
              : `Manage who has access to ${SHARE_SUBJECT[resourceType]} and their permission levels.`}
        </DialogDescription>
      </DialogHeader>

      {isLoading ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <div className="space-y-2 text-center">
            <ThreeDotsFade className="fill-foreground mx-auto size-4 opacity-50" />
            <p className="text-muted-foreground text-sm">Loading access information...</p>
          </div>
        </div>
      ) : accessQueryError ? (
        <>
          <div className="flex min-h-[200px] items-center justify-center">
            <div className="text-center">
              <p className="text-foreground">Failed to load access information. Please try again.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setModalOpen(false)}>Close</Button>
          </DialogFooter>
        </>
      ) : canUserViewOnly ? (
        <>
          <div className="flex min-h-[200px] items-center justify-center">
            <div className="text-center">
              <p className="text-foreground">You do not have permission to manage access.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setModalOpen(false)}>Close</Button>
          </DialogFooter>
        </>
      ) : isRemoteMode ? (
        <SendingShareView
          resourceId={shareResourceId!}
          fileId={shareFileId!}
          subject={SHARE_SUBJECT[resourceType]}
          onClose={() => setModalOpen(false)}
          labelExtra={modeToggleLink}
        />
      ) : (
        <>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-visible">
            <UserSearchInput
              availableUsers={sortedAvailableUsers}
              onSelectUser={handleAddUser}
              onCreateEmail={handleAddEmail}
              isLoading={orgUsersQuery.isLoading}
              disabled={shareFileMutation.isPending || shareSystemMutation.isPending}
              labelExtra={modeToggleLink}
            />

            <div className="space-y-2">
              <div className="text-sm font-medium">{usersListHeading(sortedDisplayedUsers.length)}</div>
              {sortedDisplayedUsers.length === 0 ? (
                <div className="flex h-[100px] items-center justify-center rounded-md border">
                  <p className="text-muted-foreground text-sm">Add users above to share access</p>
                </div>
              ) : (
                <SharedUserList className="max-h-[400px] overflow-y-auto">
                  {sortedDisplayedUsers.map((user) => {
                    const isOwner = user.grant === UserGrantType.owner;
                    const isUpstreamRemoteViewOwner = user.grant === UserGrantType.upstreamRemoteOwner;
                    const isUpstreamRemoteEditOwner = user.grant === UserGrantType.upstreamRemoteEditOwner;
                    const isUpstreamRemoteOwner = isUpstreamRemoteViewOwner || isUpstreamRemoteEditOwner;
                    const isCurrentUserItem = !!currentUser && user.userId === currentUser.id;
                    // No userId means this row is a typed address (handleAddEmail), not a known
                    // user, so it's the only case worth validating as an email.
                    const isInvalidEmail = !user.userId && !!user.label && !validateEmail(user.label);

                    return (
                      <SharedUserListItem
                        key={user.userId || user.subjectId || user.label}
                        user={user}
                        isCurrentUser={isCurrentUserItem}
                        onRemove={() => handleRemoveUserAccess(user.userId!)}
                        showRemoveButton={!isOwner && !isUpstreamRemoteOwner}
                        isInvalidEmail={isInvalidEmail}
                        actionElement={
                          isOwner || isUpstreamRemoteOwner ? (
                            <div />
                          ) : (
                            <PermissionSelect
                              currentUserPermission={currentUserPermission}
                              selectedPermission={user.grant}
                              onPermissionChange={(permission) => handleUpdateUserPermission(user.userId!, permission)}
                              userId={user.userId}
                              resourceType={resourceType}
                              ariaLabel={roleControlLabel(user)}
                            />
                          )
                        }
                        rightElement={
                          isOwner ? (
                            <Badge variant="secondary">Owner</Badge>
                          ) : isUpstreamRemoteViewOwner ? (
                            <Badge variant="secondary">Receiving Connection Owner (view access)</Badge>
                          ) : isUpstreamRemoteEditOwner ? (
                            <Badge variant="secondary">Receiving Connection Owner (edit access)</Badge>
                          ) : undefined
                        }
                      />
                    );
                  })}
                </SharedUserList>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="share-popup-save-button"
              disabled={shareFileMutation.isPending || shareSystemMutation.isPending}
              onClick={handleShare}
            >
              {shareFileMutation.isPending || shareSystemMutation.isPending ? "Sharing..." : "Share"}
            </Button>
          </DialogFooter>
        </>
      )}
    </DialogContent>
  );
}

function usersListHeading(count: number): string {
  return count === 0 ? "Not shared with anyone" : `Shared with (${count})`;
}

function roleControlLabel(user: DraftUserType): string {
  const name =
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    user.displayName ||
    user.label ||
    user.email ||
    "this user";
  return `Role for ${name}`;
}
